import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const IntelligentQuiz = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizData, setQuizData] = useState({
    ownerName: '',
    email: '',
    dogName: '',
    age: '',
    weight: '',
    activityLevel: '',
    bodyCondition: '',
    digestiveIssues: [],
    dietaryRestrictions: [],
    foodRelatedIssues: [],
    mainGoal: ''
  });

  const totalSteps = 5;
  const progressPercentage = (currentStep / totalSteps) * 100;

  const updateQuizData = (field, value) => {
    setQuizData(prev => ({ ...prev, [field]: value }));
  };

  const toggleMultiSelect = (field, value) => {
    setQuizData(prev => {
      const currentArray = prev[field] || [];
      const isSelected = currentArray.includes(value);
      const newArray = isSelected 
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const submitQuiz = async () => {
    console.log('🚀 submitQuiz called!');
    console.log('Quiz data:', quizData);
    
    setIsSubmitting(true);
    try {
      console.log('Making API call to /api/submit-quiz...');
      const response = await axios.post('/api/submit-quiz', {
        quizData,
        affiliateContext: {
          source: 'direct',
          timestamp: new Date().toISOString()
        }
      });

      console.log('✅ API response:', response.data);

      if (response.data.success) {
        console.log('✅ Navigating to plan page...');
        navigate(`/plan?session_id=${response.data.session_id}`);
      }
    } catch (error) {
      console.error('❌ Quiz submission error:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(`There was an error submitting your quiz: ${error.response?.data?.message || error.message}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return quizData.ownerName && quizData.email && quizData.dogName;
      case 2:
        return quizData.age && quizData.weight && quizData.activityLevel;
      case 3:
        return quizData.bodyCondition;
      case 4:
        return true; // Allow proceeding even with no selections
      case 5:
        return quizData.mainGoal;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h2 className="quiz-title">
              Let's get to know you and your furry friend! 🐾
            </h2>
            
            <div className="quiz-question">What's your name?</div>
            <input
              type="text"
              className="quiz-input"
              placeholder="Tell us your first name"
              value={quizData.ownerName}
              onChange={(e) => updateQuizData('ownerName', e.target.value)}
            />

            <div className="quiz-question">What's your email address?</div>
            <input
              type="email"
              className="quiz-input"
              placeholder="We'll send your results here"
              value={quizData.email}
              onChange={(e) => updateQuizData('email', e.target.value)}
            />

            <div className="quiz-question">What's your dog's name?</div>
            <input
              type="text"
              className="quiz-input"
              placeholder="Your pup's name"
              value={quizData.dogName}
              onChange={(e) => updateQuizData('dogName', e.target.value)}
            />
          </div>
        );

      case 2:
        return (
          <div>
            <h2 className="quiz-title">
              Let's learn more about {quizData.dogName}
            </h2>
            
            <div className="quiz-question">How old is {quizData.dogName}?</div>
            <div className="quiz-options">
              {[
                'Puppy (under 1 year)',
                'Adult (1-7 yr)',
                'Senior (7+ yrs)'
              ].map(age => (
                <div
                  key={age}
                  className={`quiz-option ${quizData.age === age ? 'selected' : ''}`}
                  onClick={() => updateQuizData('age', age)}
                >
                  {age}
                </div>
              ))}
            </div>

            <div className="quiz-question mt-8">How much does {quizData.dogName} weigh?</div>
            <input
              type="number"
              className="quiz-input"
              placeholder="Weight in pounds"
              value={quizData.weight}
              onChange={(e) => updateQuizData('weight', e.target.value)}
            />

            <div className="quiz-question">How active is {quizData.dogName}?</div>
            <div className="quiz-options">
              {[
                'Low (Mostly indoor, minimal exercise)',
                'Moderate (Daily walks, some playtime)',
                'High (Very active, lots of exercise)'
              ].map(activity => (
                <div
                  key={activity}
                  className={`quiz-option ${quizData.activityLevel === activity ? 'selected' : ''}`}
                  onClick={() => updateQuizData('activityLevel', activity)}
                >
                  {activity}
                </div>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <div className="quiz-question">How would you describe {quizData.dogName}'s current body condition?</div>
            <div className="quiz-options">
              {[
                'Underweight (ribs easily visible)',
                'Ideal (healthy weight, ribs felt easily)',
                'Overweight (ribs hard to feel)'
              ].map(condition => (
                <div
                  key={condition}
                  className={`quiz-option ${quizData.bodyCondition === condition ? 'selected' : ''}`}
                  onClick={() => updateQuizData('bodyCondition', condition)}
                >
                  {condition}
                </div>
              ))}
            </div>

            <div className="quiz-question mt-8">Does {quizData.dogName} have any digestive issues? (select all that apply)</div>
            <div className="quiz-options">
              {[
                'Sensitive stomach',
                'Loose stool/diarrhea',
                'Excessive gas',
                'Occasional vomiting',
                'No digestive issues'
              ].map(issue => (
                <div
                  key={issue}
                  className={`quiz-option ${quizData.digestiveIssues.includes(issue) ? 'selected' : ''}`}
                  onClick={() => toggleMultiSelect('digestiveIssues', issue)}
                >
                  {issue}
                </div>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <div className="quiz-question">Does {quizData.dogName} have any dietary restrictions? (select all that apply)</div>
            <div className="quiz-options">
              {[
                'Grain-free',
                'Chicken-free',
                'Fish-free',
                'Beef-free',
                'Limited ingredient diet',
                'No restrictions'
              ].map(restriction => (
                <div
                  key={restriction}
                  className={`quiz-option ${quizData.dietaryRestrictions.includes(restriction) ? 'selected' : ''}`}
                  onClick={() => toggleMultiSelect('dietaryRestrictions', restriction)}
                >
                  {restriction}
                </div>
              ))}
            </div>

            <div className="quiz-question mt-8">Are you noticing any current food-related issues with {quizData.dogName}? (select all that apply)</div>
            <div className="quiz-options">
              {[
                'Low energy',
                'Dull coat',
                'Picky eater',
                'Food allergies',
                'Weight issues',
                'No current issues'
              ].map(issue => (
                <div
                  key={issue}
                  className={`quiz-option ${quizData.foodRelatedIssues.includes(issue) ? 'selected' : ''}`}
                  onClick={() => toggleMultiSelect('foodRelatedIssues', issue)}
                >
                  {issue}
                </div>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div>
            <div className="quiz-question">Finally, what's your main goal for {quizData.dogName}?</div>
            <div className="quiz-options">
              {[
                'Weight Loss - Help my dog lose weight',
                'Weight Gain - Help my dog gain healthy weight',
                'Maintenance - Keep current healthy weight',
                'Muscle Building - Build lean muscle',
                'Digestive Health - Improve digestion',
                'Coat Health - Improve skin and coat',
                'Joint Health - Support aging joints',
                'Energy Boost - Increase energy levels'
              ].map(goal => (
                <div
                  key={goal}
                  className={`quiz-option ${quizData.mainGoal === goal ? 'selected' : ''}`}
                  onClick={() => updateQuizData('mainGoal', goal)}
                >
                  {goal}
                </div>
              ))}
            </div>

            {/* Summary Section */}
            <div style={{ 
              marginTop: '40px', 
              padding: '20px', 
              background: '#f8fafc', 
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ color: '#1e293b', marginBottom: '15px' }}>📋 Summary of Your Answers</h4>
              <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                <p><strong>Owner:</strong> {quizData.ownerName}</p>
                <p><strong>Email:</strong> {quizData.email}</p>
                <p><strong>Dog:</strong> {quizData.dogName}</p>
                <p><strong>Age:</strong> {quizData.age}</p>
                <p><strong>Weight:</strong> {quizData.weight} lbs</p>
                <p><strong>Activity Level:</strong> {quizData.activityLevel}</p>
                <p><strong>Body Condition:</strong> {quizData.bodyCondition}</p>
                <p><strong>Digestive Issues:</strong> {quizData.digestiveIssues.length > 0 ? quizData.digestiveIssues.join(', ') : 'None selected'}</p>
                <p><strong>Dietary Restrictions:</strong> {quizData.dietaryRestrictions.length > 0 ? quizData.dietaryRestrictions.join(', ') : 'None selected'}</p>
                <p><strong>Food-Related Issues:</strong> {quizData.foodRelatedIssues.length > 0 ? quizData.foodRelatedIssues.join(', ') : 'None selected'}</p>
                <p><strong>Main Goal:</strong> {quizData.mainGoal}</p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="quiz-container">
      <Link to="/" className="back-link" data-testid="back-to-home">
        ← Back to Home
      </Link>

      <div className="quiz-card">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>

        <div className="step-indicator">
          Step {currentStep} of {totalSteps}
        </div>

        {renderStep()}

        <div className="quiz-navigation">
          <button
            className="btn btn-secondary"
            onClick={prevStep}
            disabled={currentStep === 1}
            style={{ 
              visibility: currentStep === 1 ? 'hidden' : 'visible' 
            }}
          >
            Previous
          </button>

          {currentStep === totalSteps ? (
            <button
              className="btn btn-primary"
              onClick={submitQuiz}
              disabled={!canProceed() || isSubmitting}
            >
              {isSubmitting ? 'Getting Your Results...' : 'Get My Recommendations'}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={nextStep}
              disabled={!canProceed()}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntelligentQuiz;